<template>
  <b-card v-if="assetId" no-body class="asset-plans-panel mb-1">
    <button
      type="button"
      class="panel-toggle"
      :class="{ 'is-open': isOpen }"
      :aria-expanded="isOpen ? 'true' : 'false'"
      @click="toggleOpen"
    >
      <div class="d-flex align-items-center">
        <feather-icon icon="LayersIcon" size="16" class="mr-50" />
        <div class="text-left">
          <div class="font-weight-bold">{{ $t('Asset maintenance plans') }}</div>
          <small class="text-muted">
            {{ isOpen ? $t('Click a step to fill the field') : $t('Optional. Open to pick a plan step.') }}
          </small>
        </div>
      </div>
      <div class="d-flex align-items-center">
        <b-badge v-if="overdueCount > 0" variant="danger" pill class="mr-50">{{ overdueCount }}</b-badge>
        <b-badge v-if="totalSteps > 0" variant="light-primary" pill class="mr-50">
          {{ totalSteps }} {{ $t('Steps') }}
        </b-badge>
        <feather-icon :icon="isOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="18" />
      </div>
    </button>

    <b-collapse :visible="isOpen">
      <b-card-body>
        <div v-if="loading" class="text-center py-1">
          <b-spinner small variant="primary" />
        </div>

        <div v-else-if="plans.length === 0" class="text-muted text-center py-1">
          {{ $t('No maintenance plans linked to this asset') }}
        </div>

        <div v-else>
          <b-form-input
            v-model="stepQuery"
            size="sm"
            class="mb-1"
            :placeholder="$t('Filter steps')"
          />

          <div v-if="suggestedSteps.length && !stepQuery" class="suggested-block mb-1">
            <small class="text-muted d-block mb-50">{{ $t('Suggested steps') }}</small>
            <button
              v-for="step in suggestedSteps"
              :key="`suggested-${step.id}`"
              type="button"
              class="step-row compact"
              :class="{
                'is-selected': step.id === selectedStepId,
                'is-overdue': step.status === 'overdue',
                'is-next': step.isNext,
              }"
              @click="selectStep(step)"
            >
              <div class="step-main">
                <div class="text-truncate">
                  <strong>{{ step.name }}</strong>
                  <span class="text-muted"> · {{ step.planName }}</span>
                </div>
                <small class="text-muted">{{ stepMeta(step) }}</small>
              </div>
              <b-badge v-if="step.isNext" variant="primary" pill>{{ $t('Next step') }}</b-badge>
              <b-badge v-else-if="step.status === 'overdue'" variant="danger" pill>{{ $t('Overdue:') }}</b-badge>
            </button>
          </div>

          <div v-for="plan in visiblePlans" :key="plan.id" class="plan-block">
            <button type="button" class="plan-toggle" @click="togglePlan(plan.id)">
              <span class="text-truncate">{{ plan.name }}</span>
              <span class="d-flex align-items-center">
                <small class="text-muted mr-50">{{ plan.steps.length }} {{ $t('Steps') }}</small>
                <feather-icon :icon="isPlanExpanded(plan.id) ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="14" />
              </span>
            </button>

            <b-collapse :visible="isPlanExpanded(plan.id)">
              <div class="steps-scroll">
                <button
                  v-for="step in plan.steps"
                  :key="step.id"
                  type="button"
                  class="step-row compact"
                  :class="{
                    'is-selected': step.id === selectedStepId,
                    'is-overdue': step.status === 'overdue',
                    'is-next': step.isNext,
                  }"
                  @click="selectStep(step)"
                >
                  <div class="step-main">
                    <div class="text-truncate font-weight-bold">{{ step.name }}</div>
                    <small class="text-muted">{{ stepMeta(step) }}</small>
                  </div>
                  <feather-icon
                    :icon="step.id === selectedStepId ? 'CheckIcon' : 'ChevronRightIcon'"
                    size="16"
                    :class="step.id === selectedStepId ? 'text-primary' : 'text-muted'"
                  />
                </button>
                <div v-if="plan.steps.length === 0" class="text-muted small px-1 py-50">
                  {{ $t('No matching records found') }}
                </div>
              </div>
            </b-collapse>
          </div>
        </div>
      </b-card-body>
    </b-collapse>
  </b-card>
</template>

<script>
import { computed, ref, watch } from '@vue/composition-api'
import { BBadge, BCard, BCardBody, BCollapse, BFormInput, BSpinner } from 'bootstrap-vue'
import store from '@/store'
import i18n from '@/libs/i18n'
import {
  formatMetric, mapMaintenanceItem,
} from '../../maintenance/maintenanceUtils'

export default {
  name: 'AssetPreventivePlansPanel',
  components: {
    BBadge, BCard, BCardBody, BCollapse, BFormInput, BSpinner,
  },
  props: {
    assetId: { type: String, default: '' },
    selectedStepId: { default: null },
  },
  setup(props, { emit }) {
    const isOpen = ref(false)
    const loading = ref(false)
    const hasLoaded = ref(false)
    const plans = ref([])
    const stepQuery = ref('')
    const expandedPlanIds = ref([])
    let requestToken = 0

    const totalSteps = computed(() => plans.value.reduce((count, plan) => count + plan.steps.length, 0))
    const overdueCount = computed(() => plans.value.reduce(
      (count, plan) => count + plan.steps.filter(step => step.status === 'overdue').length,
      0,
    ))

    const suggestedSteps = computed(() => {
      const all = plans.value.flatMap(plan => plan.steps)
      const next = all.filter(step => step.isNext)
      const overdue = all.filter(step => step.status === 'overdue' && !step.isNext)
      return [...next, ...overdue].slice(0, 4)
    })

    const visiblePlans = computed(() => {
      const query = stepQuery.value.trim().toLowerCase()
      if (!query) return plans.value
      return plans.value
        .map(plan => ({
          ...plan,
          steps: plan.steps.filter(step => (
            (step.name || '').toLowerCase().includes(query)
            || String(step.scheduledValue || '').includes(query)
          )),
        }))
        .filter(plan => plan.steps.length > 0)
    })

    const isPlanExpanded = planId => expandedPlanIds.value.includes(planId)

    const stepMeta = (step) => {
      const parts = []
      if (step.scheduledValue) {
        parts.push(`${i18n.t('Scheduled')}: ${formatMetric(step.scheduledValue)} ${step.unit}`)
      }
      if (step.remaining != null) {
        const label = step.remaining > 0 ? i18n.t('Remaining:') : i18n.t('Overdue:')
        parts.push(`${label} ${formatMetric(Math.abs(step.remaining))} ${step.unit}`)
      }
      return parts.join(' · ')
    }

    const togglePlan = (planId) => {
      const index = expandedPlanIds.value.indexOf(planId)
      if (index >= 0) expandedPlanIds.value.splice(index, 1)
      else expandedPlanIds.value.push(planId)
    }

    const resolveStepId = (step, searchableSteps) => {
      const byId = searchableSteps.find(item => item.preventiveMaintenanceId === step.id || item.id === step.id)
      if (byId) return byId.preventiveMaintenanceId || byId.id
      const byName = searchableSteps.find(item => item.name === step.name)
      return byName?.preventiveMaintenanceId || byName?.id || step.id
    }

    const expandDefaultPlans = (loadedPlans) => {
      const withNext = loadedPlans.find(plan => plan.steps.some(step => step.isNext))
      expandedPlanIds.value = [withNext ? withNext.id : loadedPlans[0].id]
    }

    const loadPlans = async (assetId) => {
      if (!assetId) {
        plans.value = []
        return
      }

      const token = ++requestToken
      loading.value = true

      try {
        const [plansRes, summaryRes, searchableRes] = await Promise.all([
          store.dispatch('app-management/fetchPlansByAssetId', { assetId, page: 1, perPage: 50 }),
          store.dispatch('app-management/fetchMaintenancePlanSummary', { id: assetId }),
          store.dispatch('app-management/fetchPreventiveMaintenances', {
            assetId, search: '', page: 1, perPage: 100,
          }).catch(() => ({ data: { data: [] } })),
        ])

        if (token !== requestToken) return

        const linkedPlans = plansRes.data.data || []
        const summaryItems = (summaryRes.data.data || []).map(item => mapMaintenanceItem(
          item, null, null, summaryRes.data.currentHourmeter, summaryRes.data.currentOdometer,
        ))
        const searchableSteps = searchableRes.data.data || []
        const nextKey = summaryItems[0] ? `${summaryItems[0].planName}::${summaryItems[0].name}` : null

        const details = await Promise.all(linkedPlans.map(plan => {
          const planId = plan.preventiveMaintenancePlanId || plan.id
          return store.dispatch('app-management/fetchMaintenancePlan', { id: planId })
            .then(response => ({ plan, detail: response.data }))
            .catch(() => ({ plan, detail: null }))
        }))

        if (token !== requestToken) return

        const loadedPlans = details.map(({ plan, detail }) => {
          const planName = plan.name || detail?.name || ''
          const rawSteps = detail?.preventiveMaintenances || []
          const steps = rawSteps
            .map(step => {
              const summary = summaryItems.find(item => item.planName === planName && item.name === step.name)
              const mapped = summary || mapMaintenanceItem({
                ...step,
                planName,
                hourmeterLeft: null,
                odometerLeft: null,
              }, null, null, summaryRes.data.currentHourmeter, summaryRes.data.currentOdometer)

              return {
                id: resolveStepId(step, searchableSteps),
                name: step.name,
                planName,
                scheduledValue: mapped.scheduledValue,
                remaining: summary ? mapped.remaining : null,
                status: summary ? mapped.status : null,
                unit: mapped.unit,
                isNext: nextKey === `${planName}::${step.name}`,
              }
            })
            .sort((a, b) => (a.scheduledValue || 0) - (b.scheduledValue || 0))

          return {
            id: plan.preventiveMaintenancePlanId || plan.id,
            name: planName,
            steps,
          }
        })

        plans.value = loadedPlans
        hasLoaded.value = true
        if (loadedPlans.length) expandDefaultPlans(loadedPlans)
      } catch (error) {
        if (token === requestToken) plans.value = []
      } finally {
        if (token === requestToken) loading.value = false
      }
    }

    const toggleOpen = () => {
      isOpen.value = !isOpen.value
      if (isOpen.value && !hasLoaded.value) loadPlans(props.assetId)
    }

    const selectStep = (step) => {
      emit('select', step)
      isOpen.value = false
    }

    watch(() => props.assetId, () => {
      isOpen.value = false
      hasLoaded.value = false
      plans.value = []
      stepQuery.value = ''
      expandedPlanIds.value = []
    })

    watch(stepQuery, (query) => {
      if (!query) return
      expandedPlanIds.value = visiblePlans.value.map(plan => plan.id)
    })

    return {
      isOpen,
      loading,
      plans,
      stepQuery,
      totalSteps,
      overdueCount,
      suggestedSteps,
      visiblePlans,
      toggleOpen,
      togglePlan,
      isPlanExpanded,
      selectStep,
      stepMeta,
    }
  },
}
</script>

<style lang="scss" scoped>
.asset-plans-panel {
  overflow: hidden;
}

.panel-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  background: #f7f7f9;
  border: 0;
  border-bottom: 1px solid transparent;
  padding: 0.85rem 1.25rem;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover,
  &:focus {
    background: #f0f0f3;
    outline: none;
  }

  &.is-open {
    background: #f4f4f7;
    border-bottom-color: #ebe9f1;
  }
}

.plan-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  background: #f8f8f8;
  border: 0;
  border-radius: 0.428rem;
  padding: 0.45rem 0.65rem;
  margin-top: 0.35rem;
  font-weight: 600;
  cursor: pointer;
}

.steps-scroll {
  max-height: 240px;
  overflow-y: auto;
  margin-top: 0.35rem;
}

.step-row {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  text-align: left;
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 0.428rem;
  padding: 0.4rem 0.65rem;
  margin-bottom: 0.3rem;
  cursor: pointer;

  &.compact { min-height: 44px; }
  &:hover { border-color: #7367f0; }
  &.is-selected { border-color: #7367f0; background: #f3f2fd; }
  &.is-next { border-color: #7367f0; }
  &.is-overdue { border-left: 3px solid #ea5455; }
}

.step-main {
  min-width: 0;
  flex: 1;
}
</style>
